import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { useAuth } from "../app/providers/AuthProvider";
import { Logo } from "../components/branding/Logo";
import { Button } from "../ui/Button";
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/Card";
import { Field, Input } from "../ui/Input";
import { toast } from "../ui/Toast";

type LocationState = {
  from?: string;
};

const loginSchema = z.object({
  username: z.string().min(1, "Username is required."),
  password: z.string().min(1, "Password is required."),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const state = (location.state ?? {}) as LocationState;
  const from = state.from ?? "/company";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  React.useEffect(() => {
    try {
      const expired = window.localStorage.getItem("kkp_admin_session_expired");
      if (expired) {
        window.localStorage.removeItem("kkp_admin_session_expired");
        toast.warning("Session expired. Please sign in again.", {
          title: "Session",
          durationMs: 2400,
        });
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const onSubmit = handleSubmit(async ({ username, password }) => {
    try {
      await auth.login({ username: username.trim(), password });
      toast.success("Signed in.", { title: "Welcome", durationMs: 2000 });
      navigate(from, { replace: true });
    } catch {
      toast.danger("Login failed. Please try again.", {
        title: "Error",
      });
    }
  });

  return (
    <main className="kkp-auth">
      <div className="kkp-auth_stack">
        <Logo height={86} />

        <Card className="w-full max-w-[520px] p-5">
          <CardHeader className="pb-0 mb-0 border-b-0">
            <CardTitle as="h1">Admin Login</CardTitle>
            <CardDescription>
              Please ensure you're logged into Zero Trust Network Access.
            </CardDescription>
          </CardHeader>

          <CardBody className="mt-4">
            <form className="grid gap-4" onSubmit={onSubmit}>
              <Field label="Email" required error={errors.username?.message}>
                <Input
                  autoComplete="email"
                  placeholder="name@company.com"
                  {...register("username")}
                />
              </Field>

              <Field label="Password" required error={errors.password?.message}>
                <Input
                  type="password"
                  autoComplete="current-password"
                  placeholder="*******"
                  {...register("password")}
                />
              </Field>

              <div className="kkp-auth_actions">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? "Signing in…" : "Login"}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </div>
    </main>
  );
}
